import { Button } from './primitives/Button.js';
import type { CurrencyCode } from '../hooks/useCurrency.js';
import './Header.css';

export interface HeaderProps {
  /**
   * `home` is the only view that gets in-page anchors. On `docs` and `pricing`
   * an anchor like `#systems` would set a bare hash, route the app back home,
   * and land at the top of a page it never scrolled — so those views offer a way
   * back instead.
   */
  view: 'home' | 'docs' | 'pricing' | 'legal';
  ccyCode: CurrencyCode;
  regionLabel: string;
  onCycleCurrency: () => void;
  onGoHome: () => void;
  onGoDocs: () => void;
  onOpenAudit: () => void;
  onOpenBooking: () => void;
  onToggleHelp: () => void;
}

export function Header({
  view,
  ccyCode,
  regionLabel,
  onCycleCurrency,
  onGoHome,
  onGoDocs,
  onOpenAudit,
  onOpenBooking,
  onToggleHelp,
}: HeaderProps) {
  return (
    <header className="land-header">
      <div className="land-header__bar land-container">
        <button className="land-header__logo" onClick={onGoHome} aria-label="Dedupr — home">
          <span>dedupr</span>
          <span className="land-header__dot" />
          {view === 'docs' && <span className="land-header__docs-tag">docs</span>}
          {view === 'pricing' && <span className="land-header__docs-tag">pricing</span>}
          {view === 'legal' && <span className="land-header__docs-tag">legal</span>}
        </button>

        {view === 'home' ? (
          <nav className="land-header__nav">
            <a href="#modes">How it works</a>
            <a href="#systems">What it connects to</a>
            <a href="#decide">How it decides</a>
            <a href="#pricing">Pricing</a>
            <button className="land-btn land-btn--plain" onClick={onGoDocs}>
              Docs
            </button>
          </nav>
        ) : view === 'pricing' || view === 'legal' ? (
          <nav className="land-header__nav">
            <button className="land-btn land-btn--plain" onClick={onGoDocs}>
              Docs
            </button>
          </nav>
        ) : (
          <div className="land-header__search">
            <span aria-hidden="true">⌕</span>
            <input placeholder="Search the docs" />
            <kbd>⌘K</kbd>
          </div>
        )}

        <div className={`land-header__actions${view === 'pricing' ? ' land-header__actions--pricing' : ''}`}>
          {view === 'home' ? (
            <>
              <Button
                variant="secondary"
                size="xs"
                className="land-header__ccy"
                onClick={onCycleCurrency}
                title="Prices shown in your region's currency"
              >
                <span className="land-header__ccy-dot" />
                {ccyCode} · {regionLabel}
              </Button>
              <Button variant="secondary" size="md" onClick={onOpenBooking}>
                Book a meeting
              </Button>
              <Button variant="primary" size="md" onClick={onOpenAudit}>
                Free data audit
              </Button>
            </>
          ) : view === 'pricing' ? (
            <>
              <Button
                variant="secondary"
                size="xs"
                className="land-header__ccy"
                onClick={onCycleCurrency}
                title="Prices shown in your region's currency"
              >
                <span className="land-header__ccy-dot" />
                {ccyCode} · {regionLabel}
              </Button>
              <Button
                variant="primary"
                size="md"
                className="land-header__audit"
                onClick={onOpenAudit}
              >
                Free data audit
              </Button>
              <Button variant="dark" size="sm" onClick={onGoHome}>
                Back to site
              </Button>
            </>
          ) : view === 'legal' ? (
            <Button variant="dark" size="sm" onClick={onGoHome}>
              Back to site
            </Button>
          ) : (
            <>
              <span className="land-header__version">v2026.07</span>
              <Button variant="tint" size="sm" onClick={onToggleHelp}>
                ✻ Ask Dedupr
              </Button>
              <Button variant="dark" size="sm" onClick={onGoHome}>
                Back to site
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
